/*
 * Export templates (audit Phase 3.3). Replaces
 * wwwroot/js/ExportTemplate/List.js and Detail.js on the Phase 3.1 core.
 * No jQuery.
 *
 * One screen: the saved CSV report designs of a Domain, and a designer dialog
 * over it. The designer is driven entirely by the catalog the server embeds in
 * the dialog: choosing a Kind decides which joins exist, the checked joins
 * decide which columns and which fan-outs are available, and the checked
 * columns -- in their on-screen order -- become ConfigJson at Save time.
 *
 * The designer used to be started by an inline `<script>` at the foot of
 * Detail.cshtml. A modal's body is placed with innerHTML, which never executes
 * an inserted script, so nothing hydrated: the dialog opened on "Choose a kind
 * first" and stayed there, and Save wrote an empty column list over whatever
 * the template had. It is an enhancer now, which runs wherever the partial
 * lands.
 *
 * The area used to hang off `Mcp.Admin.ExportTemplate.*` -- the compat facade's
 * root. Registering `Mcp` as an area would have turned the registry's window
 * fallback off for every remaining `Mcp.Admin.*` name (the entity picker, the
 * time-window toggle), so the views name `ExportTemplate.*` instead.
 */
import { ajaxJson, ajaxModal, ajaxSwap, failToast } from '../core/ajax';
import { all, el, one } from '../core/dom';
import { serialize } from '../core/forms';
import { confirm, hideModalThen } from '../core/modal';
import { toast } from '../core/toast';
import { isValid } from '../core/validation';

const MODAL = 'ExportTemplateModal';
const FORM = 'ExportTemplateForm';
const ALERT = 'ExportTemplateDetailMessageContainer';

// ---------------------------------------------------------------- the catalog

interface CatalogColumn { key: string; displayName?: string }
interface CatalogJoin {
    key: string;
    displayName?: string;
    description?: string;
    columns?: CatalogColumn[];
    /** Absent on pre-12.5 catalogs; absent means "yes". */
    supportsFanOut?: boolean;
}
interface CatalogKind { kind: string; displayName?: string; baseColumns?: CatalogColumn[]; availableJoins?: CatalogJoin[] }
interface Catalog { kinds?: CatalogKind[] }

/**
 * The catalog the server embedded in this dialog. Read fresh each time rather
 * than cached: the payload belongs to the dialog, and a cache outlives it.
 */
function catalog(): Catalog {
    const raw = one('#ExportTemplateCatalogPayload')?.textContent || '{}';
    try {
        const parsed: unknown = JSON.parse(raw);
        return (parsed && typeof parsed === 'object') ? parsed as Catalog : { kinds: [] };
    } catch {
        return { kinds: [] };
    }
}

function kindDescriptor(kind: string): CatalogKind | null {
    const wanted = (kind || '').toLowerCase();
    return catalog().kinds?.find(k => (k.kind || '').toLowerCase() === wanted) ?? null;
}

function selectedKind(): string {
    return (one('#ExportTemplateKind') as HTMLSelectElement | null)?.value ?? '';
}

function checkedValues(selector: string): string[] {
    return all(selector)
        .filter((node): node is HTMLInputElement => node instanceof HTMLInputElement && node.checked)
        .map(node => node.value);
}

function activeJoins(): string[] {
    return checkedValues('#ExportTemplateJoins input[type=checkbox]');
}

function selectedColumnKeys(): string[] {
    return checkedValues('#ExportTemplateColumns .export-template-column');
}

function activeFanOuts(): string[] {
    return checkedValues('#ExportTemplateFanOuts .export-template-fanout');
}

// ---------------------------------------------------------------- rendering

/** Every column the chosen Kind offers with the given joins switched on. */
function eligibleColumns(kind: string, joinKeys: string[]): CatalogColumn[] {
    const descriptor = kindDescriptor(kind);
    if (!descriptor) return [];
    const columns = [...(descriptor.baseColumns ?? [])];
    const enabled = new Set(joinKeys.map(k => (k || '').toLowerCase()));
    for (const join of descriptor.availableJoins ?? []) {
        if (enabled.has((join.key || '').toLowerCase())) columns.push(...(join.columns ?? []));
    }
    return columns;
}

function columnRow(column: CatalogColumn, checked: boolean): HTMLLIElement {
    const row = el('li', 'list-group-item d-flex align-items-center');
    const check = document.createElement('input');
    check.type = 'checkbox';
    check.className = 'form-check-input me-2 export-template-column';
    check.value = column.key;
    check.checked = checked;
    const label = el('span', 'flex-grow-1', column.displayName || column.key);
    const code = el('code', 'ms-2 text-body-secondary small', column.key);
    // Delegated, not bound to the button: a directly bound handler is fine
    // until Font Awesome swaps the <i> inside, and data-action is what every
    // other control in the console uses.
    const up = el('button', 'btn btn-sm btn-outline-secondary ms-2');
    up.type = 'button';
    up.title = 'Move up';
    up.setAttribute('data-action', 'ExportTemplate.Detail.MoveUp');
    up.innerHTML = '<i class="fas fa-arrow-up"></i>';
    const down = el('button', 'btn btn-sm btn-outline-secondary');
    down.type = 'button';
    down.title = 'Move down';
    down.setAttribute('data-action', 'ExportTemplate.Detail.MoveDown');
    down.innerHTML = '<i class="fas fa-arrow-down"></i>';
    row.append(check, label, code, up, down);
    return row;
}

/**
 * Draw the column list: the saved selection first, in its saved order, then
 * everything else the Kind and joins make eligible. A saved column that is no
 * longer eligible -- its join was switched off -- is dropped rather than shown
 * unchecked, because the export cannot produce it.
 */
function renderColumns(kind: string, joinKeys: string[], selected: string[]): void {
    const list = one('#ExportTemplateColumns');
    if (!list) return;
    const eligible = eligibleColumns(kind, joinKeys);
    const lookup = new Map(eligible.map(c => [c.key.toLowerCase(), c]));
    const rows: HTMLLIElement[] = [];
    const seen = new Set<string>();
    for (const key of selected) {
        const lower = (key || '').toLowerCase();
        const column = lookup.get(lower);
        if (!column || seen.has(lower)) continue;
        seen.add(lower);
        rows.push(columnRow(column, true));
    }
    for (const column of eligible) {
        const lower = column.key.toLowerCase();
        if (seen.has(lower)) continue;
        rows.push(columnRow(column, false));
    }
    list.replaceChildren(...rows);
}

function joinCheckbox(join: CatalogJoin, checked: boolean): HTMLDivElement {
    const wrap = el('div', 'form-check');
    const id = `ExportTemplateJoin_${join.key}`;
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.className = 'form-check-input export-template-join';
    box.id = id;
    box.value = join.key;
    box.checked = checked;
    // A checkbox dispatches its data-action on CHANGE, which is what replaces
    // the per-container jQuery delegate the legacy code bound -- twice, once in
    // KindChanged and once in HydrateFromConfig, so a hydrated dialog re-rendered
    // its column list twice on every join toggle.
    box.setAttribute('data-action', 'ExportTemplate.Detail.JoinsChanged');
    const label = el('label', 'form-check-label');
    label.htmlFor = id;
    label.append(document.createTextNode(`${join.displayName ?? join.key} — `),
        el('small', 'text-body-secondary', join.description ?? ''));
    wrap.append(box, label);
    return wrap;
}

function renderJoins(kind: string, checkedKeys: string[]): void {
    const host = one('#ExportTemplateJoins');
    if (!host) return;
    const descriptor = kindDescriptor(kind);
    if (!descriptor) {
        host.replaceChildren(el('small', 'text-body-secondary', 'Choose a kind first.'));
        return;
    }
    const checked = new Set(checkedKeys.map(k => (k || '').toLowerCase()));
    const joins = descriptor.availableJoins ?? [];
    if (joins.length === 0) {
        host.replaceChildren(el('small', 'text-body-secondary', 'This kind has no optional joins.'));
        return;
    }
    host.replaceChildren(...joins.map(join => joinCheckbox(join, checked.has((join.key || '').toLowerCase()))));
}

/**
 * Fan-out is offered only for joins that are BOTH checked and capable of it.
 * Checks that survive a join change are preserved; a fan-out on a join that
 * was just switched off would emit rows the export cannot fill.
 */
function renderFanOuts(preserve: string[]): void {
    const host = one('#ExportTemplateFanOuts');
    if (!host) return;
    const descriptor = kindDescriptor(selectedKind());
    if (!descriptor) {
        host.replaceChildren(el('small', 'text-body-secondary', 'Choose a kind first.'));
        return;
    }
    const prior = new Set(preserve.map(v => (v || '').toLowerCase()));
    const enabled = new Set(activeJoins().map(j => (j || '').toLowerCase()));
    const rows: HTMLElement[] = [];
    for (const join of descriptor.availableJoins ?? []) {
        if (!enabled.has((join.key || '').toLowerCase())) continue;
        if (join.supportsFanOut === false) continue;        // absent means yes, on legacy catalogs
        const id = `ExportTemplateFanOut_${join.key}`;
        const wrap = el('div', 'form-check');
        const box = document.createElement('input');
        box.type = 'checkbox';
        box.className = 'form-check-input export-template-fanout';
        box.id = id;
        box.value = join.key;
        box.checked = prior.has((join.key || '').toLowerCase());
        const label = el('label', 'form-check-label', join.displayName ?? join.key);
        label.htmlFor = id;
        wrap.append(box, label);
        rows.push(wrap);
    }
    host.replaceChildren(...(rows.length > 0
        ? rows
        : [el('small', 'text-body-secondary', 'No fan-out-capable joins are checked. Pick a join above to enable fan-out.')]));
}

// ---------------------------------------------------------------- enhancer

interface SavedConfig { joins?: string[]; columns?: string[]; fanOuts?: string[]; fanOut?: string }

function parseConfig(raw: string): SavedConfig {
    if (!raw) return {};
    try {
        const parsed: unknown = JSON.parse(raw);
        return (parsed && typeof parsed === 'object') ? parsed as SavedConfig : {};
    } catch {
        return {};
    }
}

/**
 * Start the designer from what the server rendered: the saved ConfigJson for
 * an edit, nothing for a create. Runs wherever the dialog lands, which is the
 * whole point -- the inline initialiser it replaces never ran at all, because
 * a modal body is placed with innerHTML.
 */
export function enhanceExportTemplateDesigner(root: ParentNode): void {
    const form = root.querySelector<HTMLElement>(`#${FORM}`);
    if (!form || form.dataset['mcpExportDesigner'] === '1') return;
    form.dataset['mcpExportDesigner'] = '1';

    const config = parseConfig((one('#ExportTemplateConfigJson') as HTMLInputElement | null)?.value ?? '');
    const kind = selectedKind();
    renderJoins(kind, config.joins ?? []);
    renderColumns(kind, config.joins ?? [], config.columns ?? []);
    // fanOuts is the 12.5 list-shaped field; pre-12.5 templates stored one
    // fanOut string.
    renderFanOuts(config.fanOuts?.length ? config.fanOuts : (config.fanOut ? [config.fanOut] : []));
}

// ---------------------------------------------------------------- handlers

export const List = {
    NewTemplate(domainId?: unknown): void {
        void ajaxModal({
            modal: MODAL,
            url: `ExportTemplate/Create/${Number(domainId) || 0}`,
            focusId: 'ExportTemplateName',
            errorTitle: 'New Export Template',
            errorMessage: 'Failed to render new template form',
        });
    },

    Edit(templateId: number | string): void {
        void ajaxModal({
            modal: MODAL,
            url: `ExportTemplate/Detail/${templateId}`,
            focusId: 'ExportTemplateName',
            errorTitle: 'Edit Export Template',
            errorMessage: 'Failed to render template detail',
        });
    },

    Delete(templateId: number | string): void {
        void confirm({
            title: 'Delete Export Template',
            message: 'Delete this export template? Exports already downloaded are unaffected; the design '
                + 'itself cannot be recovered.',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `ExportTemplate/${templateId}`,
                    method: 'DELETE',
                    errorTitle: 'Failed to delete template',
                    onSuccess: () => {
                        toast('success', 'Template deleted');
                        void List.Reload();
                    },
                    onFailure: failToast('Failed to delete template'),
                });
            },
        });
    },

    Reload(): Promise<void> {
        const domainId = (one('#CurrentDomainId') as HTMLInputElement | null)?.value || '0';
        return ajaxSwap({
            container: 'ViewContainer',
            url: `ExportTemplate/Domain/${domainId}`,
            alertTitle: 'Failed to reload templates',
        });
    },
};

export const Detail = {
    /** The Kind select changed: joins, columns and fan-outs all start over. */
    KindChanged(): void {
        const kind = selectedKind();
        renderJoins(kind, []);
        renderColumns(kind, [], []);
        renderFanOuts([]);
    },

    /** A join was toggled: redraw the columns and the fan-out options it governs. */
    JoinsChanged(): void {
        renderColumns(selectedKind(), activeJoins(), selectedColumnKeys());
        renderFanOuts(activeFanOuts());
    },

    /** `this` is the arrow button; the row is its parent <li>. */
    MoveUp(this: Element): void {
        const row = this.closest('li');
        const previous = row?.previousElementSibling;
        if (row && previous) previous.before(row);
    },

    MoveDown(this: Element): void {
        const row = this.closest('li');
        const next = row?.nextElementSibling;
        if (row && next) next.after(row);
    },

    Save(): void {
        const form = one(`#${FORM}`) as HTMLFormElement | null;
        if (!form) return;
        const configField = one('#ExportTemplateConfigJson') as HTMLInputElement | null;
        if (configField) configField.value = buildConfigJson();
        if (!isValid(form)) return;
        const id = Number.parseInt((one('#ExportTemplateId') as HTMLInputElement | null)?.value ?? '0', 10) || 0;
        void ajaxJson({
            url: id > 0 ? `ExportTemplate/${id}` : 'ExportTemplate',
            method: id > 0 ? 'PUT' : 'POST',
            data: serialize(form),
            alertInto: ALERT,
            errorTitle: 'Failed to save template',
            onSuccess: () => {
                toast('success', id > 0 ? 'Template saved' : 'Template created');
                hideModalThen(MODAL, () => void List.Reload());
            },
            onFailure: failToast('Failed to save template'),
        });
    },
};

/** The designer's state, in the shape the exporter reads. */
export function buildConfigJson(): string {
    const payload: { joins: string[]; columns: string[]; fanOuts?: string[] } = {
        joins: activeJoins(),
        columns: selectedColumnKeys(),
    };
    const fanOuts = activeFanOuts();
    if (fanOuts.length > 0) payload.fanOuts = fanOuts;
    return JSON.stringify(payload);
}
